import { useCallback, useEffect, useState } from 'react';
import type { CoreOrganization } from '@/lib/zifekCore';
import {
  applySeed,
  fetchSeedStats,
  listSeedRegistry,
  seedCatalogMeta,
  seedStatusMeta,
  type CoreSeedEntry,
} from '@/lib/coreSeedData';

interface Props {
  commerceId: number;
  org: CoreOrganization;
}

const DEFAULT_COUNTS: Record<string, number> = {
  countries: 195, currencies: 32, languages: 12, main_categories: 24, standard_roles: 5, permissions: 48, statuses: 18,
};

export default function SeedDataTab({ org }: Props) {
  const [entries, setEntries] = useState<CoreSeedEntry[]>([]);
  const [stats, setStats] = useState({ total: 0, applied: 0, pending: 0, items: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [list, s] = await Promise.all([listSeedRegistry(), fetchSeedStats()]);
      setEntries(list);
      setStats(s);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Chargement impossible.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const apply = async (e: CoreSeedEntry) => {
    setBusy(e.seed_key);
    setError('');
    try {
      await applySeed(e.seed_key, e.item_count && e.item_count > 0 ? e.item_count : (DEFAULT_COUNTS[e.seed_key] || 0));
      setNotice(`${seedCatalogMeta(e.seed_key).label} appliqué.`);
      await load();
    } catch (err) { setError(err instanceof Error ? err.message : 'Erreur.'); } finally { setBusy(null); }
  };

  return (
    <div className="space-y-5">
      <p className="text-xs text-foreground-500 max-w-3xl">
        <span className="font-semibold text-foreground-700">Seed / Default data</span> : le socle fournit des
        données initiales prêtes à l’emploi — pays, devises, langues, catégories principales, rôles standards,
        permissions et statuts — pour que chaque entreprise soit opérationnelle dès la première seconde.
      </p>

      {error && <div className="p-3 bg-red-50 border border-red-200 rounded-md text-xs text-red-700">{error}</div>}
      {notice && <div className="p-3 bg-secondary-50 border border-secondary-200 rounded-md text-xs text-secondary-800 flex items-center justify-between"><span>{notice}</span><button onClick={() => setNotice('')} className="cursor-pointer"><i className="ri-close-line"></i></button></div>}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="rounded-lg border bg-background-50 border-background-200/70 p-4">
          <i className="ri-seedling-line text-lg text-primary-600"></i>
          <p className="text-lg font-bold text-foreground-900 mt-1">{stats.total}</p>
          <p className="text-xs text-foreground-500">Jeux de données</p>
        </div>
        <div className="rounded-lg border bg-background-50 border-background-200/70 p-4">
          <i className="ri-checkbox-circle-line text-lg text-secondary-600"></i>
          <p className="text-lg font-bold text-foreground-900 mt-1">{stats.applied}</p>
          <p className="text-xs text-foreground-500">Appliqués</p>
        </div>
        <div className="rounded-lg border bg-background-50 border-background-200/70 p-4">
          <i className="ri-time-line text-lg text-accent-600"></i>
          <p className="text-lg font-bold text-foreground-900 mt-1">{stats.pending}</p>
          <p className="text-xs text-foreground-500">À appliquer</p>
        </div>
        <div className="rounded-lg border bg-background-50 border-background-200/70 p-4">
          <i className="ri-database-2-line text-lg text-primary-600"></i>
          <p className="text-lg font-bold text-foreground-900 mt-1">{stats.items.toLocaleString('fr-FR')}</p>
          <p className="text-xs text-foreground-500">Éléments couverts</p>
        </div>
      </div>

      <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-background-200/60 flex items-center gap-2">
          <span className="w-8 h-8 rounded-lg bg-primary-100 text-primary-700 flex items-center justify-center"><i className="ri-seedling-line"></i></span>
          <h4 className="text-sm font-semibold text-foreground-900">Données initiales du socle</h4>
        </div>
        {loading ? (
          <div className="flex items-center justify-center py-12"><i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i></div>
        ) : (
          <div className="divide-y divide-background-200/60">
            {entries.map((e) => {
              const meta = seedCatalogMeta(e.seed_key);
              const sm = seedStatusMeta(e.status);
              return (
                <div key={e.seed_key} className="px-4 py-3 flex items-center gap-3 flex-wrap">
                  <span className="w-9 h-9 rounded-lg bg-background-100 text-foreground-700 flex items-center justify-center flex-shrink-0"><i className={meta.icon}></i></span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-foreground-800 truncate">{e.label || meta.label}</p>
                    <p className="text-[11px] text-foreground-400 truncate">{e.category} · {e.item_count || 0} éléments · v{e.version}{e.applied_at ? ` · ${new Date(e.applied_at).toLocaleDateString('fr-FR')}` : ''}</p>
                  </div>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full whitespace-nowrap ${sm.className}`}>{sm.label}</span>
                  {e.status !== 'applied' && (
                    <button onClick={() => apply(e)} disabled={busy === e.seed_key} className="px-2.5 py-1 rounded-full bg-primary-100 text-primary-900 text-[11px] font-medium whitespace-nowrap cursor-pointer disabled:opacity-40 flex items-center gap-1">
                      {busy === e.seed_key ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-play-line"></i>} Appliquer
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}